import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import TourIcon from '@mui/icons-material/Flag'
import { ReactElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectRoles,
  selectBoundTreeStatus,
} from 'src/v3/settings/settings.slice'
import {
  selectTourCompletions,
  selectTourCompletionsStatus,
  tourStarted,
  fetchTourCompletions,
} from 'src/v3/tours/tours.slice'
import axios from 'src/utilities/http-client'
import { tourRegistry } from 'src/v3/tours/index'
import { TourDefinition } from 'src/v3/tours/types'
import BasePage from '../layout'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let roles = useAppSelector(selectRoles)
  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)
  let completions = useAppSelector(selectTourCompletions)
  let completionsStatus = useAppSelector(selectTourCompletionsStatus)
  let [hintTourID, setHintTourID] = useState<string | null>(null)

  let handleStart = (tour: TourDefinition) => {
    if (tour.startingRoute) {
      navigate(tour.startingRoute)
    } else if (!tour.launchHint) {
      dispatch(tourStarted(tour.id))
    }
    // launchHint tours can't be launched from here — usePageTour handles it
  }

  let handleRetake = (tour: TourDefinition) => {
    axios
      .delete(`/api/tours/completions/${tour.id}`)
      .then(() => dispatch(fetchTourCompletions()))
      .then(() => {
        if (tour.startingRoute) {
          navigate(tour.startingRoute)
        } else if (tour.launchHint) {
          setHintTourID(tour.id)
        } else {
          dispatch(tourStarted(tour.id))
        }
      })
      .catch(() => {})
  }

  let accessibleTours = tourRegistry.filter(
    (tour) =>
      (boundTreeStatus !== 'limited' || tour.allowInLimited) &&
      (!tour.requiredRoles ||
        roles.some((r) => tour.requiredRoles!.includes(r))),
  )

  if (completionsStatus !== 'fulfilled') {
    return (
      <BasePage>
        <Stack spacing={2}>
          {accessibleTours.map((tour) => (
            <Skeleton
              key={tour.id}
              variant="rounded"
              sx={{
                height: 72,
              }}
            />
          ))}
        </Stack>
      </BasePage>
    )
  }

  return (
    <BasePage>
      <Stack spacing={2}>
        {accessibleTours.length === 0 && (
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            No tours available.
          </Typography>
        )}
        {accessibleTours.map((tour) => {
          let completion = completions[tour.id]
          let isTaken = !!completion
          let isUpdated = isTaken && completion.completedVersion < tour.version

          let isDynamic = !tour.startingRoute && !!tour.launchHint
          let showHint = hintTourID === tour.id

          return (
            <Box key={tour.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'action.selected',
                    flexShrink: 0,
                  }}
                >
                  <TourIcon
                    fontSize="small"
                    sx={{
                      opacity: 0.7,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {tour.name}
                    </Typography>
                    {isUpdated && (
                      <Chip
                        color="primary"
                        label="Updated"
                        size="small"
                        sx={{
                          fontSize: 11,
                        }}
                      />
                    )}
                    {!isTaken && (
                      <Chip
                        color="secondary"
                        label="New"
                        size="small"
                        sx={{
                          fontSize: 11,
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ opacity: 0.6, mt: 0.25 }}>
                    {tour.description}
                  </Typography>
                </Box>
                <Button
                  variant={isTaken ? 'outlined' : 'contained'}
                  size="small"
                  disabled={!isTaken && isDynamic}
                  onClick={() =>
                    isTaken ? handleRetake(tour) : handleStart(tour)
                  }
                  sx={{ flexShrink: 0, textTransform: 'none' }}
                >
                  {isTaken ? 'Retake' : 'Start'}
                </Button>
              </Paper>
              {(showHint || (!isTaken && isDynamic)) && tour.launchHint && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {tour.launchHint}
                </Alert>
              )}
            </Box>
          )
        })}
      </Stack>
    </BasePage>
  )
}
